import React from "react";
import { Lightbox, LightboxContent } from "@/components/UI/lightbox";
import { RootPanel } from "./comps/RootPanel";

/**
 * CascadeFieldPicker — cascading drilldown replacement for the flat
 * SearchableLightbox in the reports widget config forms.
 *
 * Hosted on the shared UI/lightbox `Lightbox` with `anchorEl` (D1): the root
 * panel positions against the DataFieldInputSelector card, and every nested
 * folder panel (DrilldownList / PanelItem) opens as a floating cascade panel
 * whose dismissal is React-tree scoped — so clicking into a nested panel never
 * closes the picker (which the DynamicOptionsLightboxShell could not guarantee).
 *
 * Single-select by design: reports pick a whole FIELD (or annotation sub-theme),
 * never a field value. `onChange` fires with the ORIGINAL option object verbatim
 * (parity by reference, D3); the caller closes the picker.
 *
 * @param {HTMLElement} anchorEl        card element to anchor against
 * @param {boolean}     open            controlled open state
 * @param {Function}    onOpenChange    (open:boolean) => void
 * @param {Array}       options         EXISTING grouped/flat reports options
 * @param {Object}      value           currently selected ORIGINAL option
 * @param {Function}    onChange        (originalOption) => void
 * @param {string}      placement       root placement (default 'right-start')
 */
export const CascadeFieldPicker = ({
  anchorEl,
  open,
  onOpenChange,
  options = [],
  value,
  onChange,
  emptyMessage,
  // Open to the RIGHT of the card (into the canvas), matching the old flat
  // SearchableLightbox which positioned at anchor.right; nested folders then
  // cascade further right. The config sidebar is the narrow left sidebar, so a
  // downward panel would cover sidebar content and read as the wrong direction.
  placement = "right-start",
  // multilayer => the V3 filter's cascading-lightbox look: nested folder panels
  // render as side-by-side inline columns in one growing container (see
  // AsideFilter/LightboxFilterHeader). PanelItem folders inherit this from
  // context, so they portal in as child columns automatically.
  multilayer = true,
}) => {
  return (
    <Lightbox
      open={open}
      onOpenChange={onOpenChange}
      anchorEl={anchorEl}
      placement={placement}
      multilayer={multilayer}
      enabled
    >
      {/* trapFocus off at the root so the anchor card keeps focus and the search
          input is immediately usable; nested folder panels keep their default. */}
      <LightboxContent cascade trapFocus={false} style={{ zIndex: 100000 }}>
        <RootPanel
          options={options}
          value={value}
          onSelect={onChange}
          emptyMessage={emptyMessage}
        />
      </LightboxContent>
    </Lightbox>
  );
};

export default CascadeFieldPicker;
